'use client';

import type React from 'react';
import Image from 'next/image';

import { Button } from '@simulmedia/ui-kit';
import { urlFor } from '@skybeam/shared-sanity';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import { utils } from '../../../_components/utils';
import type {
  ResponsivePadding,
  ResponsiveValue,
  SanityImage,
} from '../../../_components/utils/types';
import { SignupLinkWithUTM } from '../../../_components/sign-up-link-with-UTM';

export type CtaWithImageSectionProps = {
  backgroundColor?: string;
  buttonLink: {
    blank?: boolean;
    href: string;
    nofollow?: boolean;
  };
  buttonText: string;
  containerPadding?: ResponsivePadding;
  contentMaxWidth?: ResponsiveValue;
  description?: string;
  heading: string;
  headingHighlight?: string;
  headingHighlightColor?: string;
  image: SanityImage;
  subline?: string;
};

export const CtaWithImageSection = ({
  backgroundColor = '#fafaf5',
  buttonLink,
  buttonText,
  containerPadding,
  contentMaxWidth,
  description,
  heading,
  headingHighlight,
  headingHighlightColor,
  image,
  subline,
}: CtaWithImageSectionProps) => {
  const imageUrl = image?.asset && urlFor(image.asset).width(900).url();
  const containerPaddingStyle =
    utils.responsiveUtils.getResponsivePaddingStyleTag(containerPadding);
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );
  const styleTags = [
    containerPaddingStyle.styleTag,
    contentMaxWidthStyle.styleTag,
  ].filter((tag): tag is React.ReactElement => tag !== null);

  return (
    <>
      {styleTags}
      <div
        className={cn(
          'tablet:py-16 laptop:py-20 desktop:py-24 flex w-full justify-center px-4 py-12',
          containerPaddingStyle.className,
        )}
        style={{ backgroundColor }}
      >
        <div
          className={cn(
            'desktop:flex-row desktop:justify-between laptop:gap-y-14 flex w-full flex-col items-center gap-y-10',
            contentMaxWidthStyle.className,
          )}
        >
          <div className="tablet:px-8 desktop:max-w-[560px] desktop:items-start desktop:px-0 desktop:text-left laptop:gap-y-6 flex flex-col items-center gap-y-4 px-4 text-center">
            <h2 className="heading-marketing-2 font-light">
              {headingHighlight?.trim() &&
              heading.includes(headingHighlight.trim()) ? (
                <>
                  {heading.slice(0, heading.indexOf(headingHighlight.trim()))}
                  <span
                    {...utils.highlightSpanProps.getHighlightSpanProps(
                      headingHighlightColor,
                    )}
                  >
                    {headingHighlight.trim()}
                  </span>
                  {heading.slice(
                    heading.indexOf(headingHighlight.trim()) +
                      headingHighlight.trim().length,
                  )}
                </>
              ) : (
                heading
              )}
            </h2>
            {description && (
              <div className="tablet:text-lg text-base text-amber-950">
                {description}
              </div>
            )}
            {subline && (
              <div className="text-base font-bold text-amber-950">
                {subline}
              </div>
            )}
            <SignupLinkWithUTM
              href={buttonLink?.href}
              rel="noopener noreferrer"
            >
              <Button className="px-8 py-2.5" variant="primary" size="lg">
                {buttonText}
              </Button>
            </SignupLinkWithUTM>
          </div>
          {imageUrl && (
            <Image
              className="tablet:max-w-[445px]"
              src={imageUrl}
              alt={image.alt ?? ''}
              width={889}
              height={889}
            />
          )}
        </div>
      </div>
    </>
  );
};
